import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { SubmitToLabsDialogContent } from '../../../computersAndVirtualMachines/types';
import { EmailValidator } from '../../../../ui/gzForm/validators/email.validator';
import { DOMEventDispatcher } from '../../../../utils/event.utils';
import { GzDialogEvents } from '../../../../ui/gzDialog/component/GzDialog';
import { InputWidthValues } from '../../../../ui/gzInput';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { TemplateState } from '../../../../types/common/types';

export class SubmitToLabsWebComponent extends LitElement {
  @state()
  private translations = {
    product: null,
    fullName: null,
    email: null,
    fileName: null,
    password: null,
    sensitiveFiles: null,
    submit: null,
    cancel: null,
  };
  protected stringsTranslator: BaseWebComponentTranslator = BaseWebComponentTranslator.getInstance();
  private commonStrings: any;

  @query('.wrapper')
  private readonly wrapper: HTMLElement;

  @query('#product-field')
  private readonly productField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

  @query('#fullName-field')
  private readonly fullNameField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#email-field')
  private readonly emailField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#filename-field')
  private readonly filenameField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#password-field')
  private readonly passwordField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#sensitive-files-field')
  private readonly sensitiveFilesField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#email-helper-text')
  private readonly emailHelperText: HTMLElement;

  @query('#password-helper-text')
  private readonly passwordHelperText: HTMLElement;

  @query('#sensitive-helper-text')
  private readonly sensitiveHelperText: HTMLElement;

  @state()
  public isValid = false;

  @state()
  private readonly formGroup: FormGroup = new FormGroup({});

  @state()
  private inputs: SubmitToLabsDialogContent;

  public setFields(data: SubmitToLabsDialogContent) {
    this.inputs = data;
  }

  static get styles() {
    return styles;
  }

  protected firstUpdated(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    this.setTranslations();
    this.setFormGroup();
    this.setValidators();
    this.setDropdown();
    this.setReadonly();
    this.setRequired();
  }

  private setDropdown(): void {
    this.productField.Input.options = this.inputs.product;
    this.productField.Input.setFormValue(this.inputs.product[0].value);
    this.productField.validate(true);
  }

  private setReadonly(): void {
    this.filenameField.readonly = true;
    if (this.inputs.product.length === 1) {
      this.productField.disabled = true;
    }
  }

  private setTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = {
      product: translateFn('Product'),
      fullName: translateFn('Full name'),
      email: translateFn('Email'),
      fileName: translateFn('File name'),
      password: translateFn('Retrieved file password'),
      sensitiveFiles: translateFn('Sensitive file'),
      submit: translateFn('Submit'),
      cancel: translateFn('Cancel'),
    };

    this.emailHelperText.innerText = translateFn('The result of the analysis will be delivered to this email address.');
    this.passwordHelperText.innerText = translateFn('Provide the password used when retrieving the file.');
    this.sensitiveHelperText.innerText = translateFn('Select the checkbox if the file you are submitting contains sensitive information.');
  }

  private setFormGroup(): void {
    this.formGroup.addFormChild(['product'], this.productField);
    this.formGroup.addFormChild(['fullName'], this.fullNameField);
    this.formGroup.addFormChild(['email'], this.emailField);
    this.formGroup.addFormChild(['filename'], this.filenameField);
    this.formGroup.addFormChild(['password'], this.passwordField);
    this.formGroup.addFormChild(['sensitiveFiles'], this.sensitiveFilesField);
  }

  private setValidators(): void {
    this.productField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
    ];

    this.fullNameField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(
        /^[^\\/:"*?<>|]*$/,
        translateFn('The name cannot contain special characters \\ / : * ? " < > |'),
      ),
    ];

    this.emailField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      EmailValidator(translateFn('Please provide a valid email address.')),
    ];

    this.filenameField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(
        /^[^\\/:"*?<>|]*$/,
        translateFn('The file name cannot contain the following special characters: \\ / : * ? " < > |'),
      ),
    ];

    this.passwordField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
    ];

    this.formGroup.valid$.subscribe({
      next: valid => {
        this.isValid = valid;
      },
    });
  }

  private setRequired(): void {
    this.productField.required = true;
    this.fullNameField.required = true;
    this.emailField.required = true;
    this.passwordField.required = true;
  }

  private submit(): void {
    if (this.isValid) {
      if (this.inputs.product.length === 1) {
        this.productField.disabled = false;
      }
      const details = this.formGroup.value;
      if (this.inputs.product.length === 1) {
        this.productField.disabled = true;
      }
      DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CONFIRM, details);
    }
  }

  private cancel(): void {
    DOMEventDispatcher.dispatchGlobalEvent(this, GzDialogEvents.CANCEL);
  }

  private renderLoading(): TemplateResult {
    return html`
      <div class='loading-container'>
        <gz-progress-spinner size='large'></gz-progress-spinner>
      </div>
    `;
  }

  private renderSubmitButton(): TemplateResult {
    return html`
      <gz-button data-confirm id='confirm' ?disabled=${!this.isValid} @click=${this.submit}>
        ${this.translations.submit}
      </gz-button>
    `;
  }

  public setLoadingState(): void {
    this.wrapper.classList.remove(TemplateState.LOADING);
    if (!this.wrapper.classList.contains(TemplateState.LOADING)) {
      this.wrapper.classList.add(TemplateState.LOADING);
    }
  }

  public removeLoadingState(): void {
    this.wrapper.classList.remove(TemplateState.LOADING);
  }

  render() {
    return html`
      <div class="wrapper">
        ${this.renderLoading()}
        <div class="content-wrapper">
          <gz-form-field id='product-field' class='ml-5 mt-5' label='${this.translations.product}'>
            <gz-dropdown
              gz-form-input
              type='dropdown'
              inputWidth='${InputWidthValues.LARGE}'
            ></gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id='fullName-field' class='ml-5' label='${this.translations.fullName}'>
            <gz-input
              gz-form-input
              value='${this.inputs.fullName}'
              placeholder='${translateFn('Enter full name')}'
              inputWidth='${InputWidthValues.LARGE}'
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id='email-field' class='ml-5' label='${this.translations.email}'>
            <gz-input
              gz-form-input
              value='${this.inputs.email}'
              placeholder='${translateFn('Enter email address')}'
              inputWidth='${InputWidthValues.LARGE}'
            ></gz-input>
            <gz-input-error></gz-input-error>
            <div id='email-password-helper-wrapper'>
              <p id='email-helper-text'></p>
            </div>
          </gz-form-field>
          <gz-form-field id='filename-field' class='ml-5' label='${this.translations.fileName}'>
            <gz-input
              gz-form-input
              value='${this.inputs.filename}'
              readonly
              inputWidth='${InputWidthValues.LARGE}'
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id='password-field' class='ml-5' label='${this.translations.password}'>
            <gz-input
              data-password
              gz-form-input
              type='password'
              placeholder='${translateFn('Enter the password of the archive')}'
              inputWidth='${InputWidthValues.LARGE}'
            ></gz-input>
            <gz-input-error></gz-input-error>
            <div id='email-password-helper-wrapper'>
              <p id='password-helper-text'></p>
            </div>
          </gz-form-field>
          <gz-form-field id='sensitive-files-field' class='ml-5'>
            <gz-checkbox
              gz-form-input
              id='checkbox-input'
              label='${this.translations.sensitiveFiles}'
            ></gz-checkbox>
            <div id='sensitive-helper-wrapper'>
              <p id='sensitive-helper-text'></p>
            </div>
          </gz-form-field>

            <div class='footer__container'>
              ${this.renderSubmitButton()}
              <gz-button data-cancel id='cancel' type='outline' color='secondary' @click=${this.cancel}>
                ${this.translations.cancel}
              </gz-button>
            </div>
        </div>
      </div>
    `;
  }
}
